import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import useProperty from 'hooks/Offers/useProperty'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import type { DrawerHotelPropertyOverview } from './types'

function mapPropertyToDrawerOverview(
  property: App.Property.Property,
): DrawerHotelPropertyOverview {
  return {
    description: property.description,
    additionalDescription: property.additionalDescription,
    popularFacilities: property.popularFacilities,
    facilities: property.facilities,
    amenityGroups: property.amenities,
  }
}

/**
 * Resolves the canonical accommodation content used by the regular offer page.
 *
 * Offer detail normally supplies this overview directly. The property lookup is a
 * fallback for mapped SPP offers so the drawer does not fall back to legacy offer
 * amenities when that optional enrichment is missing.
 */
export default function useDrawerPropertyOverview(
  offerId: string,
  offerType: string | undefined,
  offerDetailOverview: DrawerHotelPropertyOverview | undefined,
): {
  propertyOverview: DrawerHotelPropertyOverview | undefined
  isLoading: boolean
} {
  const mappingOfferType = offerType === OFFER_TYPE_BED_BANK
    ? OFFER_TYPE_BED_BANK
    : OFFER_TYPE_ALWAYS_ON
  const canUsePropertyContent = (
    offerType === OFFER_TYPE_ALWAYS_ON || offerType === OFFER_TYPE_BED_BANK
  )
  const shouldResolveProperty = canUsePropertyContent && !offerDetailOverview
  const [propertyMapping, mappingFetching] = usePropertyOfferMapping(
    mappingOfferType,
    offerId,
    { disabled: !shouldResolveProperty },
  )
  const hasValidMapping = offerType === OFFER_TYPE_BED_BANK
    ? !!propertyMapping?.bedbankPropertyIds?.includes(offerId)
    : !!propertyMapping
  const [property, propertyFetching] = useProperty(propertyMapping?.propertyId, {
    disabled: !shouldResolveProperty || !hasValidMapping,
  })

  return {
    propertyOverview: offerDetailOverview ?? (
      property ? mapPropertyToDrawerOverview(property) : undefined
    ),
    isLoading: shouldResolveProperty && (
      mappingFetching || (hasValidMapping && propertyFetching)
    ),
  }
}
